import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "prosemirror-state"
import { Decoration, DecorationSet } from "prosemirror-view"
import { Node as ProseMirrorNode } from "prosemirror-model"

import { commandDefs } from "./querySyntaxTokens"
import { matchLeadingCommand, scanLines } from "./querySyntaxScan"

/** Exported so tests can read the decorations this extension produces. */
export const querySyntaxPluginKey = new PluginKey("querySyntax")

interface DocLine {
  /** Document position of the line's first character. */
  start: number
  text: string
}

export const QuerySyntaxHighlight = Extension.create({
  name: "querySyntaxHighlight",

  addProseMirrorPlugins() {
    const createDecorations = (doc: ProseMirrorNode): DecorationSet => {
      const decorations: Decoration[] = []

      // Collect the whole script first: symbol colouring needs to see a
      // DEFINE/FOR anywhere in the document, not just the current line.
      const lines: DocLine[] = []
      doc.descendants((node: ProseMirrorNode, pos: number) => {
        if (!node.isText || !node.text) return
        let currentPos = pos
        node.text.split("\n").forEach((line: string) => {
          lines.push({ start: currentPos, text: line })
          currentPos += line.length + 1
        })
      })

      scanLines(lines.map((line) => line.text)).forEach((classes, index) => {
        const { start, text } = lines[index]

        // The scanner paints one class per character, so equal-class runs
        // become one decoration each and never overlap.
        let runStart = 0
        for (let i = 1; i <= text.length; i++) {
          if (i < text.length && classes[i] === classes[runStart]) continue
          const className = classes[runStart]
          if (className) {
            decorations.push(
              Decoration.inline(start + runStart, start + i, {
                class: className,
              })
            )
          }
          runStart = i
        }

        // Parameter check — live-editor only, and the one decoration that
        // deliberately stacks: it lands on top of the command's own class,
        // so `.syntax-error` must stay last in the stylesheet to win.
        const leading = matchLeadingCommand(text)
        const commandDef = leading ? commandDefs[leading.command] : undefined
        if (!leading || !commandDef) return
        const expectedParams = commandDef.params.length
        const providedArgs = leading.argParts.length
        if (providedArgs >= expectedParams) return
        decorations.push(
          Decoration.inline(
            start + leading.indent,
            start + leading.indent + leading.command.length,
            {
              class: "syntax-error",
              title: `Error! Expected ${expectedParams} parameters, got ${providedArgs}. Required: ${commandDef.params.join(
                ", "
              )}`,
            }
          )
        )
      })

      return DecorationSet.create(doc, decorations)
    }

    return [
      new Plugin({
        key: querySyntaxPluginKey,
        state: {
          init: (_, { doc }) => createDecorations(doc),
          apply: (tr, oldState) => {
            if (tr.docChanged) {
              return createDecorations(tr.doc)
            }
            return oldState.map(tr.mapping, tr.doc)
          },
        },
        props: {
          decorations(state) {
            return this.getState(state)
          },
        },
      }),
    ]
  },
})

/**
 * Console palette — tuned for the console's white surface (`--qc-bg`), not
 * for a dark editor. Every colour clears WCAG AA (4.5:1) on #ffffff; the
 * ratio is noted per rule. The dark docs panel restates all of these in
 * docs.css, which wins on specificity.
 *
 * Hue architecture: purple = commands (verbs), red = control flow and
 * conditions, blue = data references, teal = literal values (strings,
 * FOR/DEFINE values, types, booleans), green = labels, orange = user-defined
 * symbols, gray = punctuation and comments.
 *
 * Declaration order mirrors the scanner's paint order. The scanner emits
 * non-overlapping runs, so this is documentation rather than a dependency —
 * except for `.syntax-error`, which is stacked on top of a command's own
 * decoration and therefore must stay last.
 */
const styles = `
  .syntax-comment { color: #6E7781; font-style: italic; }          /* 4.55 */
  .syntax-command { color: #8250DF; font-weight: bold; }           /* 5.05 */
  .syntax-keyword { color: #CF222E; font-weight: bold; }           /* 5.36 */
  .syntax-type { color: #0E7490; }                                 /* 5.36 */
  .syntax-boolean { color: #0E7490; }                              /* 5.36 */
  .syntax-condition { color: #CF222E; }                            /* 5.36 */
  .syntax-clause { color: #CF222E; }                               /* 5.36 */
  .syntax-qtablePath { color: #0550AE; }                           /* 7.59 */
  .syntax-string { color: #0E7490; }                               /* 5.36 */
  .syntax-ref-punct { color: #6E7781; }                            /* 4.55 */
  .syntax-ref-label { color: #116329; font-style: italic; }        /* 7.39 */
  .syntax-cat-label { color: #116329; }                            /* 7.39 */
  .syntax-operator { color: #57606A; }                             /* 6.39 */
  .syntax-symbol { color: #953800; }                               /* 7.39 */
  .syntax-symbol-def { color: #953800; font-weight: bold; }        /* 7.39 */
  .syntax-error { color: #D1242F; text-decoration: wavy underline; } /* 5.24 */
`

const styleElement = document.createElement("style")
styleElement.textContent = styles
document.head.appendChild(styleElement)

export default QuerySyntaxHighlight
